import React, { memo, useCallback, useMemo, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { areEqual } from 'Utils/equalityChecks';
import { formatCurrency, formatDate, convertWordsFirstLetterUppercase } from 'Utils/helpers';
import { TabContent } from 'Components/Tabs';
import { Spinner } from 'Components/Spinner';
import { Table, TableBody, TableColumn, TableHeader, TableRow, Th } from 'Components/Table';
import { Icon } from 'Components/Icons';
import { PurpleButton } from 'Components/Button';
import DatePicker from 'react-datepicker';
import classes from './rocketPayReport.module.css';
import 'react-datepicker/dist/react-datepicker-min.module.css';

interface Props {
  payments?: any[];
  isLoading?: boolean;
}

function getPaymentDate(payment: any): Date | null {
  // Use paid_at (stamped on payment.succeeded webhook) for completed payments;
  // fall back to created_at (request creation date) for pending/unpaid ones.
  const rawDate = payment.paid_at || payment.created_at;

  if (!rawDate) {
    return null;
  }

  const parsedDate = new Date(rawDate);
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
}

function RocketPayReport({ payments = [], isLoading = false }: Props) {
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
  const [startDate, endDate] = dateRange;

  const filteredPayments = useMemo(() => {
    if (!startDate && !endDate) {
      return payments;
    }

    return payments.filter((payment) => {
      const paymentDate = getPaymentDate(payment);
      if (!paymentDate) {
        return false;
      }

      if (startDate && paymentDate < startDate) {
        return false;
      }

      if (endDate) {
        const inclusiveEndDate = new Date(endDate);
        inclusiveEndDate.setHours(23, 59, 59, 999);

        if (paymentDate > inclusiveEndDate) {
          return false;
        }
      }

      return true;
    });
  }, [payments, startDate, endDate]);

  const summary = useMemo(() => {
    const totalAmountCents = filteredPayments.reduce((sum, payment) => sum + (payment.amount || 0), 0);

    return {
      transactionCount: filteredPayments.length,
      totalAmountCents,
    };
  }, [filteredPayments]);

  const chartData = useMemo(() => {
    const grouped = filteredPayments.reduce((acc: Record<string, any>, payment) => {
      const paymentDate = getPaymentDate(payment);
      const sortKey = paymentDate ? formatDate(paymentDate, 'yyyy-MM-dd') : 'unknown';
      const dateKey = paymentDate ? formatDate(paymentDate, 'MMM d') : 'Unknown';

      if (!acc[sortKey]) {
        acc[sortKey] = { name: dateKey, totalAmount: 0, count: 0, sortKey };
      }

      acc[sortKey].totalAmount += (payment.amount || 0) / 100;
      acc[sortKey].count += 1;

      return acc;
    }, {});

    return Object.values(grouped).sort((a: any, b: any) => a.sortKey.localeCompare(b.sortKey));
  }, [filteredPayments]);

  const hasPayments = filteredPayments.length > 0;

  const onDateRangeChange = useCallback((dates: [Date | null, Date | null]) => {
    setDateRange(dates);
  }, []);

  const clearDateFilter = useCallback(() => {
    setDateRange([null, null]);
  }, []);

  return (
    <TabContent key="tab-content-rocketpay-report" id="rocketpayreport" className="position-relative">
      <div className={classes.rocketPayContent}>
        <div className={classes.contentHeader}>
          <h3>Payment Report</h3>
          <p>All RocketPay transactions with a daily amount trend and transaction-level details.</p>
        </div>

        <div className={classes.filterBar}>
          <div className={classes.filterControls}>
            <span className={classes.filterLabel}>Date Range</span>
            <div className={classes.datePickerWrapper}>
              <DatePicker
                todayButton="Today"
                selected={startDate}
                onChange={onDateRangeChange}
                startDate={startDate}
                endDate={endDate}
                maxDate={new Date()}
                selectsRange
                isClearable
                placeholderText="Select date range..."
                className={classes.dateInput}
              />
            </div>
            {(startDate || endDate) && (
              <PurpleButton className={classes.clearFilterButton} onClick={clearDateFilter}>
                Clear
              </PurpleButton>
            )}
          </div>
          <span className={classes.filterSummary}>
            {summary.transactionCount} {summary.transactionCount === 1 ? 'record' : 'records'}
          </span>
        </div>

        <div className={classes.metricsGrid}>
          <div className={classes.metricCard}>
            <span className={classes.metricLabel}>Transactions</span>
            <strong>{summary.transactionCount}</strong>
          </div>
          <div className={classes.metricCard}>
            <span className={classes.metricLabel}>Total Volume</span>
            <strong>{formatCurrency(summary.totalAmountCents / 100, 'USD')}</strong>
          </div>
        </div>

        <div className={classes.chartCard}>
          <div className={classes.cardHeader}>
            <h4>Transaction Volume</h4>
            <span>Grouped by day</span>
          </div>
          {hasPayments ? (
            <div className={classes.chartWrapper}>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis tickFormatter={(value) => `$${value}`} />
                  <Tooltip
                    formatter={(value: number, _name, item: any) => [
                      formatCurrency(value, 'USD'),
                      `${item?.payload?.count || 0} transactions`,
                    ]}
                  />
                  <Bar dataKey="totalAmount" fill="#6d00e6" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className={classes.emptyState}>No payment transactions available.</p>
          )}
        </div>

        <div className={classes.tableCard}>
          <div className={classes.cardHeader}>
            <h4>Transaction Details</h4>
            <span>{summary.transactionCount} records</span>
          </div>

          {hasPayments ? (
            <Table className={`table ${classes.paymentListWrapper}`}>
              <TableHeader>
                <TableRow>
                  <Th>Date</Th>
                  <Th>Email</Th>
                  <Th>Amount</Th>
                  <Th>Status</Th>
                  <Th>Description</Th>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPayments.map((payment) => (
                  <TableRow key={payment.id}>
                    <TableColumn>{getPaymentDate(payment) ? formatDate(getPaymentDate(payment), 'MMM d, yyyy h:mma') : '-'}</TableColumn>
                    <TableColumn>{payment.email || '-'}</TableColumn>
                    <TableColumn>
                      {formatCurrency((payment.amount || 0) / 100, payment.currency || 'USD')}{' '}
                      {(payment.currency || 'USD').toUpperCase()}
                    </TableColumn>
                    <TableColumn>{convertWordsFirstLetterUppercase(payment.payment_response || 'Pending')}</TableColumn>
                    <TableColumn>{payment.description || '-'}</TableColumn>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className={classes.noPaymentContent}>
              <p className={classes.noPaymentText}>No payment transactions found.</p>
              <Icon type="rocketemblemtwo" />
            </div>
          )}
        </div>
        <Spinner loading={isLoading} />
      </div>
    </TabContent>
  );
}

RocketPayReport.defaultProps = {
  payments: [],
  isLoading: false,
};

const RocketPayReportMemo = memo(RocketPayReport, areEqual);

export { RocketPayReportMemo as RocketPayReport };
